import React from "react";
import {
  Alert,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { Download } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";

const BoardReflections = () => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = (documentURL) => {
    if (documentURL) {
      window.open(
        `${process.env.REACT_APP_API_URL}/api/dev/admin/board-reflection/download?filename=${documentURL}`,
        "_blank"
      );
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["boardReflections"],
    queryFn: async () => {
      return await UserQuery.getAllBoardReflections();
    }
  });

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data?.boardReflections || data.boardReflections.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography
          fontWeight="bolder"
          fontSize={20}
          sx={{ color: "primary.main", textTransform: "uppercase" }}
        >
          Board Reflections
        </Typography>
        <Divider />
        <Alert severity="info">
          No board reflections available at the moment. Please check back later.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography
        fontWeight="bolder"
        fontSize={20}
        sx={{ color: "primary.main", textTransform: "uppercase" }}
      >
        Board Reflections
      </Typography>
      <Divider />
      <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
        <Table aria-label="board reflections table">
          <TableHead sx={{ backgroundColor: "primary.main" }}>
            <TableRow>
              <TableCell
                align="center"
                sx={{ fontWeight: "bolder", color: "#FFFFFF" }}
              >
                No#
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bolder", color: "#FFFFFF" }}
              >
                Title
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bolder", color: "#FFFFFF" }}
              >
                Period
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bolder", color: "#FFFFFF" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? (data?.boardReflections || []).slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
              : data?.boardReflections || []
            ).map((item, i) => (
              <TableRow
                key={item.id}
                sx={{ backgroundColor: i % 2 === 0 ? "action.hover" : "" }}
              >
                <TableCell align="center" component="th" scope="row">
                  {i + 1}
                </TableCell>
                <TableCell align="center" component="th" scope="row">
                  {item.title}
                </TableCell>
                <TableCell align="center" component="th" scope="row">
                  {item.period}
                </TableCell>
                <TableCell align="center" scope="row">
                  <Tooltip title="Download">
                    <IconButton
                      sx={{
                        backgroundColor: "primary.main",
                        color: "#FFFFFF",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "#FFFFFF",
                          fontWeight: "bolder"
                        }
                      }}
                      onClick={() => {
                        handleDownload(item.documentURL);
                      }}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, { label: "All", value: -1 }]}
                count={data?.boardReflections?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    "aria-label": "rows per page"
                  },
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default BoardReflections;
